import { INLINE_LABELS } from "./labels.ts";
import { Dialog } from "@base-ui/react/dialog";
import { Menu } from "@base-ui/react/menu";
import { Switch as BaseSwitch } from "@base-ui/react/switch";
import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import { CaretRightIcon, CheckCircleIcon, CheckIcon, CopyIcon, DotsThreeIcon, InfoIcon, SidebarSimpleIcon, TrayIcon, WarningIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type ButtonHTMLAttributes, Children, cloneElement, createContext, Fragment, isValidElement, type ReactNode, type Ref, type RefObject, useContext, useId, useState } from "react";
import { ApiError } from "./api.ts";
import { reasonText, SHELL_LABELS, STEP_STATUS, COMMON_LABELS } from "./labels.ts";
import { number } from "./format.ts";
import { notify, wasNotified } from "./feedback.ts";
import { Select } from "./ui/select.tsx";

// The building blocks of every page (docs/design-system.md). Look comes from
// styles.css; behaviour (focus, keyboard, screen readers) from Base UI.

// ------------------------------------------------------------ shell context

export interface Shell {
  collapsed: boolean;
  toggleSidebar: () => void;
}
export const ShellContext = createContext<Shell>({ collapsed: false, toggleSidebar: () => {} });

// ------------------------------------------------------------ page frame

export interface Crumb {
  label: string;
  to?: string;
  params?: Record<string, string>;
}

/**
 * The bar on top of the panel: where you are, and what you can do here
 * (docs/design-system.md §0.2). With the sidebar hidden it also shows it again.
 */
export function TopBar({ crumbs, actions }: { crumbs: Crumb[]; actions?: ReactNode }) {
  const shell = useContext(ShellContext);
  return (
    <div className="z-topbar">
      <div className="z-pagebar">
        {shell.collapsed && <button type="button" className="z-iconbtn" aria-label={SHELL_LABELS.showSidebar} title={SHELL_LABELS.showSidebar} onClick={shell.toggleSidebar}>
          <SidebarSimpleIcon size={16} />
        </button>}
        <nav className="z-crumbs" aria-label={COMMON_LABELS.breadcrumb}>
          {crumbs.map((crumb, index) => <Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && <CaretRightIcon size={12} className="z-crumb-sep" aria-hidden />}
            {crumb.to && index < crumbs.length - 1 ? <Link to={crumb.to} params={crumb.params} className="z-crumb">{crumb.label}</Link> : <span className="z-crumb" aria-current={index === crumbs.length - 1 ? "page" : undefined}>{crumb.label}</span>}
          </Fragment>)}
        </nav>
        {actions && <div className="z-topbar-actions">{actions}</div>}
      </div>
    </div>
  );
}

/**
 * A page: the top bar, then a column of blocks under a title. `actions` go in
 * the top bar; anything that belongs to one section goes in that section.
 */
export function Page({ crumbs, title, meta, actions, wide, children }: { crumbs: Crumb[]; title?: ReactNode; meta?: ReactNode; actions?: ReactNode; wide?: boolean; children: ReactNode }) {
  return (
    <>
      <TopBar crumbs={crumbs} actions={actions} />
      <div className={`z-page${wide ? " wide" : ""}`}>
        {title !== undefined && (
          <header className="z-head">
            <h1>{title}</h1>
            {meta && <div className="z-head-meta">{meta}</div>}
          </header>
        )}
        {children}
      </div>
    </>
  );
}

export function Section({ id, title, count, note, actions, children }: { id?: string; title: ReactNode; count?: number; note?: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="z-section" id={id}>
      <div className="z-section-head">
        <h2>{title}</h2>
        {count !== undefined && <span className="z-count">{count}</span>}
        {actions && <span className="z-section-actions">{actions}</span>}
      </div>
      {note && <p className="z-section-note">{note}</p>}
      {children}
    </section>
  );
}

// ------------------------------------------------------------ tabs


/** Sections of one page shown one at a time, instead of one long scroll. */
export function Tabs<T extends string>({ value, onValueChange, tabs, tools, children }: { value: T; onValueChange: (v: T) => void; tabs: { value: T; label: string; count?: number; disabled?: boolean }[]; tools?: ReactNode; children: ReactNode }) {
  return (
    <BaseTabs.Root className="z-tabset" value={value} onValueChange={(v) => onValueChange(v as T)}>
      <BaseTabs.List className="z-tabs">
        {tabs.map((t) => (
          <BaseTabs.Tab key={t.value} value={t.value} disabled={t.disabled} className="z-tab">
            {t.label}
            {t.count !== undefined && <span className="z-count">{t.count}</span>}
          </BaseTabs.Tab>
        ))}
        {tools && <div className="z-tabs-tools">{tools}</div>}
      </BaseTabs.List>
      {children}
    </BaseTabs.Root>
  );
}

export function TabPanel({ value, children }: { value: string; children: ReactNode }) {
  return (
    <BaseTabs.Panel value={value} className="z-panel">
      {children}
    </BaseTabs.Panel>
  );
}

// ------------------------------------------------------------ blocks

export function Button({
  tone,
  size,
  busy,
  icon,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { ref?: Ref<HTMLButtonElement>; tone?: "primary" | "soft" | "ghost" | "danger"; size?: "sm"; busy?: boolean; icon?: ReactNode }) {
  return (
    <button type="button" {...rest} disabled={rest.disabled || busy} className={["z-btn", tone, size, rest.className].filter(Boolean).join(" ")}>
      {icon && <span className="z-btn-icon" aria-hidden>{icon}</span>}
      {busy ? INLINE_LABELS.working : children}
    </button>
  );
}

export const Pill = ({ tone = "gray", status, children }: { tone?: string; status?: boolean; children: ReactNode }) => (
  <span className={`z-pill${status ? " status" : ""}`} data-tone={tone}>
    {status && <i />}
    {children}
  </span>
);

export const Empty = ({ children }: { children: ReactNode }) => <p className="z-empty">{children}</p>;

/** A collection-level state with a useful next action, not a blank dashboard. */
export function EmptyState({ title, children, icon, actions, tone = "gray" }: { title: ReactNode; children?: ReactNode; icon?: ReactNode; actions?: ReactNode; tone?: string }) {
  return <div className="z-empty-state" data-tone={tone}>
    <span className="z-object-icon" aria-hidden>{icon ?? <TrayIcon size={24} />}</span>
    <div className="z-empty-state-body"><h3>{title}</h3>{children && <div className="z-empty-state-note">{children}</div>}{actions && <div className="z-empty-state-actions">{actions}</div>}</div>
  </div>;
}

/** A single task or form group. Semantic tones also have visible text. */
export function Surface({ children, tone, className = "" }: { children: ReactNode; tone?: string; className?: string }) {
  return <div className={`z-surface ${className}`} data-tone={tone}>{children}</div>;
}


export function ViewSwitch<T extends string>({ value, onValueChange, options, label }: { value: T; onValueChange: (value: T) => void; options: { value: T; label: string; icon?: ReactNode }[]; label: string }) {
  return <div className="z-segmented z-view-switch" role="group" aria-label={label}>{options.map((option) => <button key={option.value} type="button" className={value === option.value ? "is-on" : undefined} aria-pressed={value === option.value} onClick={() => onValueChange(option.value)}>{option.icon}{option.label}</button>)}</div>;
}

export function StageSteps({ steps }: { steps: { label: string; status: "complete" | "current" | "upcoming"; description?: ReactNode }[] }) {
  return <ol className="z-stage-steps">{steps.map((step, index) => <li key={`${index}-${step.label}`} data-state={step.status} aria-current={step.status === "current" ? "step" : undefined}>
    <span className="z-step-marker" aria-hidden>{step.status === "complete" ? <CheckIcon size={14} weight="bold" /> : number(index + 1)}</span>
    <div><span className="z-step-label">{step.label}</span><span className="z-sr"> — {STEP_STATUS[step.status]}</span>{step.description && <div className="z-step-note">{step.description}</div>}</div>
  </li>)}</ol>;
}

export function Callout({ tone, children }: { tone?: "warn" | "bad" | "good"; children: ReactNode }) {
  return (
    <div className={`z-callout ${tone ?? ""}`}>
      {tone === "warn" || tone === "bad" ? <WarningIcon size={18} /> : <InfoIcon size={18} />}
      <div className="z-callout-body">{children}</div>
    </div>
  );
}

/** "Nothing needs you" is a state with reasons, not an empty page. */
export function Quiet({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="z-callout z-quiet">
      <CheckCircleIcon size={20} weight="fill" />
      <div className="z-callout-body">
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export function Reasons({ reasons, inline }: { reasons: C.Reason[]; inline?: boolean }) {
  if (!reasons.length) return null;
  if (inline) return <>{reasons.map(reasonText).join(" · ")}</>;
  return (
    <ul className="z-points">
      {reasons.map((r, i) => (
        <li key={`${r.code}-${i}`}>{reasonText(r)}</li>
      ))}
    </ul>
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error || wasNotified(error)) return null;
  const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
  return (
    <p className="z-error" role="alert">
      {message}
    </p>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <BaseSwitch.Root className="z-switch" checked={checked} onCheckedChange={onChange} aria-label={label}>
      <BaseSwitch.Thumb className="z-switch-thumb" />
    </BaseSwitch.Root>
  );
}

export function Field({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) {
  const generatedId = useId();
  const fields = Children.toArray(children);
  const control = fields.find((child) => isValidElement(child) && (child.type === Select || ["input", "select", "textarea"].includes(String(child.type))));
  const controlId = isValidElement<{ id?: string }>(control) ? control.props.id ?? generatedId : undefined;
  const helpId = `${generatedId}-help`;
  const labelId = `${generatedId}-label`;
  return (
    <div className="z-field">
      <label id={labelId} htmlFor={controlId}>{label}</label>
      {fields.map((child) => isValidElement<{ id?: string; "aria-describedby"?: string; "aria-labelledby"?: string }>(child) && child === control ? cloneElement(child, {
        id: controlId,
        "aria-labelledby": child.props["aria-labelledby"] ?? (child.type === Select ? labelId : undefined),
        "aria-describedby": [child.props["aria-describedby"], help ? helpId : undefined].filter(Boolean).join(" ") || undefined,
      }) : child)}
      {help && <span className="z-help" id={helpId}>{help}</span>}
    </div>
  );
}

/** A modal with a title, a body and a footer. */
export function Modal({ open, onOpenChange, title, children, footer, finalFocus, className }: { className?: string; open: boolean; onOpenChange: (v: boolean) => void; title: string; children: ReactNode; footer?: ReactNode; finalFocus?: RefObject<HTMLElement | null> | (() => HTMLElement | false) }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        {/* The shell's drawer root makes page modals nested even while closed. */}
        <Dialog.Backdrop className="z-dialog-backdrop" forceRender />
        <Dialog.Popup className={`z-dialog${className ? ` ${className}` : ""}`} finalFocus={finalFocus}>
          <Dialog.Title render={<h2 />}>{title}</Dialog.Title>
          {children}
          {footer && <div className="z-dialog-actions">{footer}</div>}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Copies text and says so for a moment. */
export function CopyButton({ text, label = COMMON_LABELS.copy }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      tone="ghost"
      icon={done ? <CheckIcon /> : <CopyIcon />}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          notify.success(COMMON_LABELS.copied);
          setTimeout(() => setDone(false), 1500);
        } catch { setDone(false); notify.error(COMMON_LABELS.copyFailed); }
      }}
    >
      {done ? COMMON_LABELS.copied : label}
    </Button>
  );
}

export interface MenuEntry {
  label: string;
  icon?: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/** Page-level actions that are not the main one live behind "…". */
export function MoreMenu({ label = COMMON_LABELS.moreActions, items }: { label?: string; items: (MenuEntry | "sep")[] }) {
  const visible = items.filter((i, n) => i !== "sep" || (n > 0 && n < items.length - 1));
  if (!visible.some((i) => i !== "sep")) return null;
  return (
    <Menu.Root>
      <Menu.Trigger className="z-iconbtn" aria-label={label} title={label}>
        <DotsThreeIcon size={20} weight="bold" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={4} align="end">
          <Menu.Popup className="z-layer">
            {visible.map((item, i) =>
              item === "sep" ? (
                <div key={`sep-${i}`} className="z-menu-sep" />
              ) : (
                <Menu.Item key={item.label} className={`z-menu-item${item.danger ? " danger" : ""}`} disabled={item.disabled} onClick={item.onSelect}>
                  {item.icon}
                  {item.label}
                </Menu.Item>
              ),
            )}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

// ------------------------------------------------------------ dashboard

/** One number with its label. A missing number is "–", never zero. */
export function Stat({ label, value, of, note }: { label: string; value: ReactNode; of?: ReactNode; note?: ReactNode }) {
  return (
    <div className="z-stat">
      <div className="z-stat-label">{label}</div>
      <div className="z-stat-value">
        {value ?? "–"}
        {of !== undefined && <small> {of}</small>}
      </div>
      {note && <div className="z-stat-note">{note}</div>}
    </div>
  );
}

export interface Series {
  key: string;
  label: string;
  color: string;
}

export interface BarDatum {
  label: string;
  /** A missing value is null: a dashed box with "–", never a zero bar. */
  values: Record<string, number | null>;
  /** Not over yet, or not every day observed: striped. */
  partial?: boolean;
  /** Read out and shown on hover after the label, such as the days observed. */
  detail?: string;
  /** What happened in this period, such as the actions that went live: a mark under the bar, its text on hover and read out. */
  marks?: string[];
}

/**
 * Grouped bars, one group per period (docs/design-system.md §0.8, §5.3).
 * Plain HTML so text keeps its size at any width; colours come from tokens
 * and each series means one thing. With few periods each bar carries its
 * value; with many, only some periods are labelled.
 */
export function BarChart({ title, note, series, data, format = number, marksNote }: { title: ReactNode; note?: ReactNode; series: Series[]; data: BarDatum[]; format?: (value: number) => string; marksNote?: ReactNode }) {
  const max = Math.max(1, ...data.flatMap((d) => series.map((s) => d.values[s.key] ?? 0)));
  const dense = data.length > 14;
  const every = Math.ceil(data.length / 7);
  const text = (v: number | null) => (v == null ? "–" : format(v));
  const hasMarks = data.some((d) => d.marks?.length);
  return (
    <figure className={`z-chart${dense ? " is-dense" : ""}`}>
      <div className="z-chart-head">
        <h3>{title}</h3>
        {series.length > 1 && series.map((s) => (
          <span key={s.key} className="z-legend">
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        {note && <span className="z-chart-note">{note}</span>}
      </div>
      <div className="z-bars" role="img" aria-label={data.map((d) => [d.label, ...series.map((s) => `${series.length > 1 ? `${s.label} ` : ""}${text(d.values[s.key] ?? null)}`), d.detail, ...(d.marks ?? [])].filter(Boolean).join(" ")).join("; ")}>
        {data.map((d, index) => (
          <div key={d.label} className="z-bar-group" title={[[d.label, d.detail].filter(Boolean).join(" · "), ...(d.marks ?? [])].join("\n")}>
            <div className="z-bar-stack">
              {series.map((s) => {
                const v = d.values[s.key] ?? null;
                if (v == null) return <span key={s.key} className={`z-bar is-missing${d.partial ? " is-pending" : ""}`}><span>–</span></span>;
                return <span key={s.key} className={`z-bar${d.partial ? " is-partial" : ""}`} style={{ height: `${(Math.max(v, 0) / max) * 100}%`, ["--z-bar" as string]: s.color }}>
                  {!dense && <span className="z-bar-value">{text(v)}</span>}
                </span>;
              })}
            </div>
            <div className="z-bar-label">{!dense || (data.length - 1 - index) % every === 0 ? d.label : "\u00a0"}</div>
            {hasMarks && <div className="z-bar-mark" aria-hidden>{d.marks?.length ? <i /> : null}</div>}
          </div>
        ))}
      </div>
      {hasMarks && marksNote && <figcaption className="z-chart-marks"><i aria-hidden />{marksNote}</figcaption>}
    </figure>
  );
}

// ------------------------------------------------ control plane (design system §0.8)

/** A keyboard shortcut hint: small monospace text on the callout colour. */
export const Kbd = ({ children }: { children: ReactNode }) => <kbd className="z-kbd">{children}</kbd>;

/**
 * The zooi.ai wordmark (design system §0.13), the same as the website's:
 * "zooi" in the display face and ".ai" quieter. It is text, so screen
 * readers read it as written; the oo mark it replaces is retired.
 */
export const Wordmark = ({ size = 20 }: { size?: number }) => (
  <span className="z-wordmark" style={{ fontSize: size }}>zooi<span>.ai</span></span>
);


/** The detail beside a list (`--z-detail-w`); full screen on narrow screens. Esc closes it. */
export function DetailPanel({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  return (
    <section className="z-detail" aria-label={label} onKeyDown={(event) => {
      if (event.key === "Escape" && !(event.target instanceof HTMLElement && event.target.closest("[role=dialog]"))) { event.preventDefault(); onClose(); }
    }}>
      {children}
    </section>
  );
}
